import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Eye, X } from "lucide-react";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { cn } from "@/lib/utils";
import { SheetClose } from "@/components/ui/sheet";

interface RecentlyViewedProps {
  currentProductId?: string;
  className?: string;
  closeOnClick?: boolean;
}

const RecentlyViewed = ({ currentProductId, className, closeOnClick = false }: RecentlyViewedProps) => {
  const { getRecentlyViewed, clearRecentlyViewed, removeFromRecentlyViewed } = useRecentlyViewed();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Filter out current product and get recent items
  const recentProducts = getRecentlyViewed()
    .filter(item => item.id !== currentProductId)
    .slice(0, 12);

  const itemsPerView = 8; // Show 8 items at once
  
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % Math.max(1, recentProducts.length - itemsPerView + 1));
  };

  const prevSlide = () => {
    setCurrentSlide(prev => prev > 0 ? prev - 1 : Math.max(0, recentProducts.length - itemsPerView));
  };

  const handleClearHistory = () => {
    clearRecentlyViewed();
  };

  const handleRemoveItem = (e: React.MouseEvent, productId: string) => {
    e.preventDefault();
    e.stopPropagation();
    removeFromRecentlyViewed(productId);
  };


  if (recentProducts.length === 0) return null;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-start gap-3">
        <Eye className="w-5 h-5 text-primary mt-1" />
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="font-boho text-xl md:text-2xl text-primary">
              Recently Viewed
            </h3>
            <Badge className="text-xs bg-primary/10 text-primary border border-primary/20">
              {recentProducts.length} item{recentProducts.length !== 1 ? 's' : ''}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleClearHistory}
              className="text-xs text-muted-foreground hover:text-destructive h-auto py-1"
            >
              Clear All
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Continue exploring pieces that caught your eye
          </p>
        </div>
      </div>

      {/* Products Carousel */}
      <div className="relative">
        {/* Navigation Buttons */}
        {recentProducts.length > itemsPerView && (
          <>
            <Button
              variant="outline"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-8 h-8 rounded-full bg-background shadow-product"
              onClick={prevSlide}
              disabled={currentSlide === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon" 
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-8 h-8 rounded-full bg-background shadow-product"
              onClick={nextSlide}
              disabled={currentSlide >= recentProducts.length - itemsPerView}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </>
        )}

        {/* Products Grid */}
        <div className="overflow-hidden">
          <div 
            className="flex transition-transform duration-300 gap-3"
            style={{ 
              transform: `translateX(-${currentSlide * (100 / itemsPerView)}%)` 
            }}
          >
            {recentProducts.map((product) => (
              <div key={product.id} className="flex-shrink-0 w-[calc(12.5%-9px)] relative group">
                {closeOnClick ? (
                  <SheetClose asChild>
                    <Link
                      to={`/shopify-product/${product.handle}`}
                      onClick={() => sessionStorage.setItem('isBackNavigation', 'true')}
                      className="aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all duration-200 block"
                    >
                      {product.image ? (
                        <img 
                          src={product.image} 
                          alt={product.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <Eye className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                    </Link>
                  </SheetClose>
                ) : (
                  <Link
                    to={`/shopify-product/${product.handle}`}
                    onClick={() => sessionStorage.setItem('isBackNavigation', 'true')}
                    className="aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all duration-200 block"
                  >
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <Eye className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                  </Link>
                )}
                {/* Remove button */}
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute -top-1 -right-1 w-4 h-4 p-0 bg-background/80 backdrop-blur-sm border border-border rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  onClick={(e) => handleRemoveItem(e, product.id)}
                >
                  <X className="w-2.5 h-2.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentlyViewed;